import React, { useEffect, useState } from 'react'
import { Col, Row, Spin } from 'antd'
import { useSearchParams } from 'react-router-dom'
import { useLazyGetWorkflowConfigQuery, useLoginToWorkflowMutation, WorkflowConfig } from './api/enrollement';
import { setCredentials } from './store/authSlice';
import { setFeatures } from './store/featuresSlice';
import { useDispatch, useSelector } from 'react-redux';
import { handleError } from './utils/errorHandler';
import EnrollmentUI from './EnrollmentUI';
import { IntlProvider } from 'react-intl';
import { browserSupportedLocales } from './types';
import { RootState } from './store';
import ErrorPage from './ErrorPage';
import { DefaultConfiguration } from './types/UIConfiguration';

const App: React.FC = () => {
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()
  const accessToken = searchParams.get('auth')?.replace(/\s/g, '+')
  const workflowId = searchParams.get('workflow')
  const networkId = searchParams.get('wlanName')
  const clientMac = searchParams.get('client_mac')
  const ssid = searchParams.get('ssid')
  const userIp = searchParams.get('uip')
  const redirectUrl = searchParams.get('url')
  const apip = searchParams.get('apip')
  const zoneName = searchParams.get('zoneName')
  const enrollmentId = sessionStorage.getItem('enrollmentId')
  const errorMessage = useSelector((state: RootState) => state.error.errorMessage);
  const [locale, setLocale] = useState('en-US')
  const [messages, setMessages] = useState()
  const [login, {isLoading, isSuccess, isError, data, error}] = useLoginToWorkflowMutation()
  const [config, setConfig] = useState<WorkflowConfig>(DefaultConfiguration)
  const [getConfig,{ isLoading: isLoadingConfig, data: uiConfig }] = useLazyGetWorkflowConfigQuery()


  useEffect(() => {
    // Validate required URL parameters
    if (!workflowId || !accessToken) {
      const missingParams = [];
      if (!workflowId) missingParams.push('workflow');
      if (!accessToken) missingParams.push('auth');
      
      handleError(
        `Missing parameters: ${missingParams.join(', ')}`, 
        `Missing required parameters. Please ensure you have the correct enrollment URL.`
      );
      return;
    }

    dispatch(setCredentials({workflowId}))
    login({accessToken, enrollmentId, cpArgs: {clientMac: clientMac, ssid: ssid, uip: userIp, redirectUrl: redirectUrl, networkId, apip, zoneName}})
  }, [])

  useEffect(() => {
    if (isSuccess && data) {
      dispatch(setCredentials({token: data.token}))
      sessionStorage.setItem('enrollmentId', data.enrollment.id)
      getConfig({})
    }
    if (isError) {
      console.error('Error loading workflow: ', error)
    }
  }, [isSuccess, isError])

  useEffect(() => {
    if (uiConfig) {
      setConfig({
        ...config,
        ...uiConfig
      })
      dispatch(setFeatures(uiConfig.features))
    }
  }, [uiConfig])

  useEffect(() => {
    const loadMessages = async () => {
      try {
        const locales = navigator.languages
        const locale = locales.find(locale =>
          browserSupportedLocales[locale as keyof typeof browserSupportedLocales]) || 'en-US'
        const messages = await import(`./locales/compiled/${locale}.json`)
        setMessages(messages.default)
        setLocale(locale)
      } catch (error) {
        console.error('Error loading messages: ', error)
      }
    }
    loadMessages()
  }, [])

  const localeOnError = (error: { code: string; }) => {
    if (error.code === 'MISSING_TRANSLATION') {
      // suppress missing translation error
      return
    }
    console.log('Error loading locale: ', error)
  }

  return (
    <IntlProvider defaultLocale='en' locale={locale} messages={messages} onError={localeOnError}>
      <div className="App" style={{height: '100vh'}}>
        <Row justify="center" style={{
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          overflowY: 'auto',
          backgroundColor: config.color.backgroundColor,
          backgroundImage: config.styles.backgroundImage ? 'url("' + config.styles.backgroundImage + '")': undefined,
          backgroundSize: 'cover',
        }}>
          <Col sm={8} xs={24} style={{height: '100vh'}}>
            {!errorMessage ? <EnrollmentUI config={config}/> : <ErrorPage message={errorMessage}/>}
          </Col>
        </Row>
        <Spin spinning={isLoading || isLoadingConfig} fullscreen/>
      </div>
    </IntlProvider>
  )
}

export default App
